/**
 * Full-screen lock overlay when isLocked. PIN mode: PinEntry → validate → unlock (or session switch).
 */

import { HTTPError } from "ky";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useAccountContext } from "@/state/providers/AccountProvider";
import { useImpersonation } from "@/state/providers/ImpersonationProvider";
import { useLock } from "@/state/providers/LockProvider";
import { useCurrentUserHasPin } from "@/state/hooks/useCurrentUserHasPin";
import { useLockScreenDeviceAccessSettings } from "@/state/hooks/useDeviceAccess";
import Spinner from "@common/components/Spinner";
import { parsePinRateLimitRetrySeconds, validatePinForUnlock } from "@common/lib/api/pins";
import { supabase } from "@common/lib/supabaseClient";
import {
    DeviceAssociationStorageError,
    getDeviceAssociation,
    getDeviceAssociationSafe,
    setDeviceAssociation,
    getOrCreateDeviceId,
} from "./deviceAssociation";
import { getLockDisplayCache, setLockDisplayCache } from "./lockStorage";
import LockScreen from "./LockScreen";
import PinCreationScreen from "./PinCreationScreen";
import PinEntry from "./PinEntry";
import { useAutoLockListener } from "./useAutoLockListener";
import { useLockShortcutListener } from "./useLockShortcutListener";

export default function LockScreenOverlay() {
    useAutoLockListener();
    useLockShortcutListener();

    const { accountData, dataLoaded } = useAccountContext();
    const { isImpersonating } = useImpersonation();
    const { isLocked, unlock } = useLock();

    const assoc = getDeviceAssociationSafe();
    const accountId = assoc?.account_id;
    const deviceId = assoc?.device_id ?? "";

    const sessionAccountId =
        accountData && typeof accountData === "object" && "id" in accountData ? (accountData.id as string) : undefined;
    const {
        hasPin,
        isPinStatusSuccess,
        isPinQueryError,
        refetch: refetchHasPin,
    } = useCurrentUserHasPin(sessionAccountId);

    const { data: deviceSettings, isSuccess: deviceSettingsSuccess } = useLockScreenDeviceAccessSettings({
        isLocked,
        lockAccountId: accountId,
        sessionAccountId: sessionAccountId,
        deviceId,
    });

    const [pinValue, setPinValue] = useState("");
    const [pinError, setPinError] = useState(false);
    const [storageError, setStorageError] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [rateLimitRetrySeconds, setRateLimitRetrySeconds] = useState<number | null>(null);
    const [serverError, setServerError] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    /** Prevents duplicate POST pins/validate when onComplete fires twice before the next paint (e.g. keydown + change). */
    const pinValidateInFlightRef = useRef(false);

    // Reset state when lock screen becomes active to ensure clean state for each lock session
    useEffect(() => {
        if (isLocked) {
            setPinValue("");
            setPinError(false);
            setStorageError(false);
            setSessionExpired(false);
            setRateLimitRetrySeconds(null);
            setServerError(false);
        }
    }, [isLocked]);

    // Count down rate-limit retry seconds so message updates every second
    useEffect(() => {
        if (rateLimitRetrySeconds == null || rateLimitRetrySeconds <= 0) return;
        const id = setInterval(() => {
            setRateLimitRetrySeconds((s) => (s == null || s <= 1 ? null : s - 1));
        }, 1000);
        return () => clearInterval(id);
    }, [rateLimitRetrySeconds]);

    // Ensure device association exists when we have account data (first login)
    useEffect(() => {
        try {
            const assoc = getDeviceAssociation();
            if (assoc?.account_id) return;
            const id =
                accountData && typeof accountData === "object" && "id" in accountData
                    ? (accountData.id as string)
                    : undefined;
            const name =
                accountData && typeof accountData === "object" && "clinic_name" in accountData
                    ? (accountData.clinic_name as string)
                    : undefined;
            const location =
                accountData && typeof accountData === "object" && "clinic_location" in accountData
                    ? (accountData.clinic_location as string | null)
                    : null;
            if (id && name !== undefined) {
                setDeviceAssociation({ account_id: id, clinic_name: name ?? "Clinic", clinic_location: location });
            }
        } catch (e) {
            if (e instanceof DeviceAssociationStorageError) setStorageError(true);
            else throw e;
        }
    }, [accountData]);

    // When locked, session may be invalidated — use cached display or device association.
    // Safe getter avoids throwing in render when localStorage is inaccessible (see deviceAssociation.ts).
    const displayCache = getLockDisplayCache();
    const assocForDisplay = getDeviceAssociationSafe();
    const clinicName =
        accountData && typeof accountData === "object" && "clinic_name" in accountData
            ? (accountData.clinic_name as string) || "Clinic"
            : (displayCache?.clinic_name ?? assocForDisplay?.clinic_name ?? "Clinic");
    const clinicLocation =
        accountData && typeof accountData === "object" && "clinic_location" in accountData
            ? (accountData.clinic_location as string | null)
            : (displayCache?.clinic_location ?? assocForDisplay?.clinic_location ?? null);

    // Persist display cache when we have session + device settings so overlay can show after sign-out
    useEffect(() => {
        if (!deviceSettingsSuccess || deviceSettings == null) return;
        const mode = deviceSettings.device_access_mode;
        if (mode !== "pin" && mode !== "switch") return;
        const name =
            accountData && typeof accountData === "object" && "clinic_name" in accountData
                ? (accountData.clinic_name as string) || "Clinic"
                : (assocForDisplay?.clinic_name ?? "Clinic");
        const loc =
            accountData && typeof accountData === "object" && "clinic_location" in accountData
                ? (accountData.clinic_location as string | null)
                : (assocForDisplay?.clinic_location ?? null);
        setLockDisplayCache({ device_access_mode: mode, clinic_name: name, clinic_location: loc });
    }, [
        deviceSettingsSuccess,
        deviceSettings,
        accountData,
        assocForDisplay?.clinic_name,
        assocForDisplay?.clinic_location,
    ]);

    const handlePinComplete = useCallback(
        async (pin: string) => {
            let assoc;
            let device_id: string;
            try {
                assoc = getDeviceAssociation();
                device_id = assoc?.device_id ?? getOrCreateDeviceId();
            } catch (e) {
                if (e instanceof DeviceAssociationStorageError) {
                    setStorageError(true);
                    return;
                }
                throw e;
            }
            const account_id = assoc?.account_id;
            if (!account_id) {
                setPinError(true);
                setPinValue("");
                return;
            }
            if (pinValidateInFlightRef.current) return;
            pinValidateInFlightRef.current = true;
            setPinError(false);
            setStorageError(false);
            setRateLimitRetrySeconds(null);
            setServerError(false);
            setIsValidating(true);
            try {
                // Session is invalid after lock(); use unauthenticated validate to get a fresh session.
                const res = await validatePinForUnlock({ account_id, pin, device_id });
                setSessionExpired(false);
                setPinValue(""); // clear so next lock shows empty PIN entry

                // Re-establish auth session with the token returned by the backend.
                if (!res.refresh_token) {
                    setSessionExpired(true);
                    return;
                }
                const { error: sessionError } = await supabase.auth.setSession({
                    access_token: res.access_token,
                    refresh_token: res.refresh_token,
                });
                if (sessionError) {
                    setSessionExpired(true);
                    return;
                }
                unlock();
            } catch (e: unknown) {
                const status =
                    e instanceof HTTPError
                        ? e.response.status
                        : (e as { response?: { status?: number } })?.response?.status;
                // POST pins/validate is unauthenticated; 401 means wrong PIN per API spec, not "session expired".
                if (status === 429) {
                    const seconds = await parsePinRateLimitRetrySeconds(e);
                    setRateLimitRetrySeconds(seconds);
                    setPinValue("");
                } else if (status != null && status >= 500) {
                    setServerError(true);
                    setPinValue("");
                } else {
                    // Wrong PIN (401/403), validation error, or other client error
                    setPinError(true);
                    setPinValue("");
                }
            } finally {
                pinValidateInFlightRef.current = false;
                setIsValidating(false);
            }
        },
        [unlock],
    );

    const handlePinChange = useCallback((value: string) => {
        setPinValue(value);
        if (value.length > 0) {
            setPinError(false);
            setServerError(false);
            setRateLimitRetrySeconds(null);
        }
    }, []);

    const handleSignInWithEmailInstead = useCallback(async () => {
        unlock();
        await supabase.auth.signOut({ scope: "local" });
    }, [unlock]);

    // Clear lock only when we have successfully loaded settings that say mode is off.
    // When session is invalid we use display cache; only run this when we have live device settings.
    useEffect(() => {
        if (!isLocked || !deviceSettingsSuccess || deviceSettings == null) return;
        const mode = deviceSettings.device_access_mode;
        if (mode !== "pin" && mode !== "switch") {
            unlock();
        }
    }, [isLocked, deviceSettingsSuccess, deviceSettings, unlock]);

    const pinsEnabled = accountData?.pins_enabled === true;
    const hasDisplayCache = displayCache != null;
    if (!pinsEnabled && !hasDisplayCache) return null;

    // Never show PIN creation while locked: stale accountData + cached hasPin:false would stack
    // PinCreationScreen above the unlock UI and createPin would fail without a session.
    // Require a successful pins/me response with has_pin === false — avoids stale default false + wrong isLoading in RQ v5.
    const showPinCreation =
        !isLocked &&
        pinsEnabled &&
        !isImpersonating &&
        dataLoaded &&
        Boolean(sessionAccountId) &&
        deviceSettings?.device_access_mode === "pin" &&
        isPinStatusSuccess &&
        !hasPin;

    const clinicNameForPinCreation =
        accountData && typeof accountData === "object" && "clinic_name" in accountData
            ? (accountData.clinic_name as string) || "Clinic"
            : "Clinic";
    const clinicLocationForPinCreation =
        accountData && typeof accountData === "object" && "clinic_location" in accountData
            ? (accountData.clinic_location as string | null)
            : null;

    const pinCreationUi =
        showPinCreation && sessionAccountId ? (
            <PinCreationScreen
                clinicName={clinicNameForPinCreation}
                clinicLocation={clinicLocationForPinCreation}
                accountId={sessionAccountId}
                onCreated={() => refetchHasPin()}
                onSignInWithEmailInstead={handleSignInWithEmailInstead}
            />
        ) : null;

    // Full-screen bridge: after invite/password we land on `/` while pins/me loads — show PIN-themed shell, not a blank app.
    const pinsMeStillLoading =
        Boolean(sessionAccountId) &&
        pinsEnabled &&
        !isLocked &&
        !isImpersonating &&
        dataLoaded &&
        deviceSettingsSuccess &&
        deviceSettings?.device_access_mode === "pin" &&
        !isPinStatusSuccess &&
        !isPinQueryError;

    let pinStatusLoadingUi: ReactNode = null;
    if (pinsMeStillLoading) {
        pinStatusLoadingUi = (
            <LockScreen
                clinicName={clinicNameForPinCreation}
                clinicLocation={clinicLocationForPinCreation}
                mode="pin"
                logo="hd-dark"
            >
                <div className="flex w-full max-w-sm flex-col items-center gap-4">
                    <Spinner />
                    <p className="text-center text-sm text-shadow-200">Checking device access…</p>
                </div>
            </LockScreen>
        );
    }

    let lockUi: ReactNode = null;
    if (isLocked) {
        // When session is invalid (after lock), we rely on display cache; when we have session, require device settings.
        const mode =
            deviceSettings != null ? deviceSettings.device_access_mode : (displayCache?.device_access_mode ?? "pin");
        if (deviceSettings != null && mode !== "pin" && mode !== "switch") {
            lockUi = null;
        } else if (deviceSettings == null && !hasDisplayCache) {
            lockUi = null;
        } else if (mode === "pin") {
            lockUi = (
                <LockScreen
                    clinicName={clinicName}
                    clinicLocation={clinicLocation}
                    mode="pin"
                    logo="hd-dark"
                    onSignInWithEmailInstead={handleSignInWithEmailInstead}
                >
                    <div className="w-full max-w-sm">
                        {storageError ? (
                            <p className="text-shadow-200 text-sm text-center" role="alert">
                                Device storage unavailable. Device remains locked.
                            </p>
                        ) : sessionExpired ? (
                            <p className="text-shadow-200 text-sm text-center" role="alert">
                                Session expired — sign in with email to continue.
                            </p>
                        ) : rateLimitRetrySeconds != null && rateLimitRetrySeconds > 0 ? (
                            <p className="text-shadow-200 text-sm text-center" role="alert">
                                Too many attempts, try again in {rateLimitRetrySeconds} second
                                {rateLimitRetrySeconds !== 1 ? "s" : ""}.
                            </p>
                        ) : (
                            <>
                                {serverError && (
                                    <p className="text-shadow-200 text-sm text-center mb-4" role="alert">
                                        Something went wrong. Please try again.
                                    </p>
                                )}
                                <PinEntry
                                    theme="dark"
                                    variant="enter"
                                    value={pinValue}
                                    onChange={handlePinChange}
                                    onComplete={handlePinComplete}
                                    hasError={pinError}
                                    disabled={
                                        isValidating || (rateLimitRetrySeconds != null && rateLimitRetrySeconds > 0)
                                    }
                                    readOnly={sessionExpired}
                                />
                            </>
                        )}
                    </div>
                </LockScreen>
            );
        } else {
            lockUi = (
                <LockScreen
                    clinicName={clinicName}
                    clinicLocation={clinicLocation}
                    mode="switch"
                    onSignInWithEmailInstead={handleSignInWithEmailInstead}
                >
                    <p className="text-shadow-300 text-sm">User Switch mode — avatar grid (coming soon).</p>
                </LockScreen>
            );
        }
    }

    if (!pinCreationUi && !lockUi && !pinStatusLoadingUi) return null;

    return (
        <>
            {lockUi}
            {pinStatusLoadingUi}
            {pinCreationUi}
        </>
    );
}
